import {CELL_OUTLINE_TEXTURE_KEYS} from '../configs/fieldCellOutlines';
import {RANGE_ZONE, RANGE_ZONE_TEXTURE_KEYS} from '../configs/fieldRangeZone';
import {GLOW_THREAD_TEXTURE_KEYS} from '../configs/glowingThreads';
import {MELEE_TEXTURE_KEYS} from '../configs/meleeAnimation';
import {CELL_OUTLINE_RADIUS_PX, ensureCellOutlineTextures} from '../utils/effects/cellOutlineTextures';
import {buildThreadGeometry, resolveThreadPoint} from '../utils/effects/glowThreadGeometry';
import {ensureGlowThreadTextures} from '../utils/effects/glowThreadTextures';
import {ensureMeleeTextures} from '../utils/effects/meleeEffectTextures';
import {buildZoneContourEdges, buildZoneContourLoops} from '../utils/effects/rangeZoneGeometry';
import {RANGE_ZONE_HATCH_RADIUS_PX, ensureRangeZoneTextures} from '../utils/effects/rangeZoneTextures';

const TWO_PI = Math.PI * 2;

const clamp01 = (value) => Math.min(1, Math.max(0, value));

/** Когда волна прочертила всю зону */
export const resolveRangeZoneRevealMs = (range) => {
  const {ringMs, revealExtraRings} = RANGE_ZONE.wave;
  return ringMs * (range + revealExtraRings);
};

/**
 * Прозрачность кольца ячеек на этом расстоянии от отряда: свой свет плюс проходящая волна
 * или слабый повторный импульс
 * @returns {number} 0..1
 */
export const resolveRingAlpha = ({elapsedMs, now, distance, range, isQuiet}) => {
  const {wave, light, ping} = RANGE_ZONE;
  const breath = 1 + light.breathScale * Math.sin((now / light.breathMs) * TWO_PI);
  const lightShare = range > 0 ? distance / range : 0;
  const lightAlpha = (light.nearAlpha + (light.farAlpha - light.nearAlpha) * lightShare)
    * breath
    * clamp01((elapsedMs - distance * wave.ringMs) / light.fadeMs);

  const waveAlpha = Math.max(0, 1 - Math.abs(elapsedMs / wave.ringMs - distance) / wave.widthRings) * wave.alpha;

  let pingAlpha = 0;
  const pingElapsedMs = elapsedMs - resolveRangeZoneRevealMs(range) - ping.delayMs;
  if (!isQuiet && pingElapsedMs > 0) {
    const pingRing = (pingElapsedMs % ping.periodMs) / ping.ringMs;
    pingAlpha = Math.max(0, 1 - Math.abs(pingRing - distance) / ping.widthRings) * ping.alpha;
  }

  return clamp01(lightAlpha + Math.max(waveAlpha, pingAlpha));
};

/**
 * Ореол контура дышит, а в момент, когда волна его прочертила, вспыхивает
 * @returns {number} 0..1
 */
export const resolveContourHaloAlpha = ({elapsedMs, now, range}) => {
  const {breathMs, minHaloAlpha, maxHaloAlpha, flashMs} = RANGE_ZONE.contour;
  const share = (Math.sin((now / breathMs) * TWO_PI) + 1) / 2;
  const flash = clamp01(1 - (elapsedMs - resolveRangeZoneRevealMs(range)) / flashMs);
  return clamp01(minHaloAlpha + (maxHaloAlpha - minHaloAlpha) * share + flash);
};

/**
 * ЗОНА ДЕЙСТВИЯ отряда (см. configs/fieldRangeZone). В отличие от остальных маркеров поля живет
 * между обновлениями стора: поле зовет show на каждое из них, а пересобирается зона только при
 * смене ячеек. Поэтому волна играет один раз, при смене отряда, и не стоит лишнего на перерисовках.
 */
export default class FieldRangeZone {
  /**
   * @param {Phaser.Scene} scene
   * @param {Object} [options]
   * @param {number} [options.linearFilter] — режим фильтрации LINEAR для текстур
   * @param {number} [options.addBlendMode] — режим смешивания сложением для света в зоне
   */
  constructor(scene, {linearFilter, addBlendMode} = {}) {
    this.scene = scene;
    this.linearFilter = linearFilter;
    this.addBlendMode = addBlendMode;
    this.zone = null;
    this.isListening = false;
    this.tick = this.tick.bind(this);
  }

  now() {
    return this.scene?.time?.now ?? 0;
  }

  /**
   * @param {Object} params
   * @param {string} params.key — чей это показ: при смене снова идет волна
   * @param {boolean} params.isQuiet — только осмотр, без повторных импульсов
   * @param {number} params.range — дальность в ячейках
   * @param {Array<{hex: Object, distance: number}>} params.cells — ячейки зоны
   * @param {Object[]} params.blockedHexes — в пределах дальности, но закрыты препятствием
   * @param {Object[]} params.outsideHexes — все остальные ячейки поля
   * @param {Array<{hex: Object, distance: number}>} params.targets — цели: вздрагивают при волне
   * @param {{ground: number, contour: number}} params.depth
   */
  show({key, isQuiet, range, cells, blockedHexes, outsideHexes, targets, depth}) {
    const signature = [cells, blockedHexes.map((hex) => ({hex}))]
      .map((list) => list.map(({hex}) => hex.key).join(';'))
      .join('|');
    if (this.zone?.key === key && this.zone.signature === signature) {
      this.zone.isQuiet = isQuiet;
      return;
    }
    // Тот же отряд с другой зоной (например, разрушили препятствие): без новой волны
    const startedAt = this.zone?.key === key ? this.zone.startedAt : this.now();
    this.hide();

    ensureGlowThreadTextures(this.scene, {linearFilter: this.linearFilter});
    ensureMeleeTextures(this.scene, {linearFilter: this.linearFilter});
    ensureCellOutlineTextures(this.scene, {linearFilter: this.linearFilter});
    ensureRangeZoneTextures(this.scene, {linearFilter: this.linearFilter});

    const hexSize = this.resolveHexSize(cells[0].hex);
    this.zone = {
      key,
      signature,
      isQuiet,
      range,
      startedAt,
      dim: this.createDim(outsideHexes, depth.ground),
      rings: this.createRings(cells, depth.ground),
      hatch: this.createHatch(blockedHexes, depth.ground, hexSize),
      ...this.createContour(cells, depth.contour, hexSize),
      pops: this.createTargetPops(targets, depth.contour, hexSize),
    };
    this.updateZone(this.zone, this.now());
    if (!this.isListening && this.scene?.events?.on) {
      this.scene.events.on('update', this.tick);
      this.isListening = true;
    }
  }

  resolveHexSize({centerX, centerY, points}) {
    return Math.hypot(points[0].x - centerX, points[0].y - centerY);
  }

  createDim(hexes, depth) {
    const dim = this.scene.add.graphics();
    dim.setDepth(depth);
    dim.fillStyle(0x000000, RANGE_ZONE.outsideDimAlpha);
    hexes.forEach((hex) => dim.fillPoints(hex.points, true));
    return dim;
  }

  createRings(cells, depth) {
    const hexesByDistance = new Map();
    cells.forEach(({hex, distance}) => {
      hexesByDistance.set(distance, [...(hexesByDistance.get(distance) || []), hex]);
    });
    return [...hexesByDistance.entries()].map(([distance, hexes]) => {
      const graphics = this.scene.add.graphics();
      graphics.setDepth(depth);
      if (this.addBlendMode !== undefined) {
        graphics.setBlendMode(this.addBlendMode);
      }
      graphics.fillStyle(RANGE_ZONE.palette.light, 1);
      hexes.forEach((hex) => graphics.fillPoints(hex.points, true));
      return {distance, graphics};
    });
  }

  createHatch(hexes, depth, hexSize) {
    const images = hexes.map((hex) => {
      const image = this.scene.add.image(hex.centerX, hex.centerY, RANGE_ZONE_TEXTURE_KEYS.HATCH);
      image.setScale(hexSize / RANGE_ZONE_HATCH_RADIUS_PX);
      image.setTint(0x000000);
      image.setAlpha(RANGE_ZONE.hatchAlpha);
      return image;
    });
    const container = this.scene.add.container(0, 0, images);
    container.setDepth(depth);
    return container;
  }

  createContour(cells, depth, hexSize) {
    const {palette, contour, motes: motesConfig} = RANGE_ZONE;
    const edges = buildZoneContourEdges(cells);
    const addStrip = (children, edge, textureKey, tint, alpha, widthFactor) => {
      const length = Math.hypot(edge.to.x - edge.from.x, edge.to.y - edge.from.y);
      const angle = Math.atan2(edge.to.y - edge.from.y, edge.to.x - edge.from.x);
      const width = hexSize * widthFactor;
      const overlap = width * contour.overlapFactor;
      const strip = this.scene.add.image(
        edge.from.x - Math.cos(angle) * overlap,
        edge.from.y - Math.sin(angle) * overlap,
        textureKey,
      );
      strip.setOrigin(0, 0.5);
      strip.setRotation(angle);
      strip.setTint(tint);
      strip.setAlpha(alpha);
      strip.setScale((length + overlap * 2) / (strip.width || 1), width / (strip.height || 1));
      children.push(strip);
    };

    // По слою на расстояние: сторона контура появляется, когда до ее ячейки дошла волна
    const distances = [...new Set(edges.map(({distance}) => distance))];
    const layers = distances.map((distance) => {
      const ofDistance = edges.filter((edge) => edge.distance === distance);
      const haloChildren = [];
      const lineChildren = [];
      ofDistance.forEach((edge) => {
        addStrip(haloChildren, edge, GLOW_THREAD_TEXTURE_KEYS.HALO, palette.halo, 1, contour.haloWidthFactor);
        addStrip(lineChildren, edge, GLOW_THREAD_TEXTURE_KEYS.HALO, palette.shadow, contour.shadowAlpha, contour.shadowWidthFactor);
      });
      ofDistance.forEach((edge) => {
        addStrip(lineChildren, edge, GLOW_THREAD_TEXTURE_KEYS.CORE, palette.core, 1, contour.coreWidthFactor);
      });
      const halo = this.scene.add.container(0, 0, haloChildren);
      halo.setDepth(depth);
      const line = this.scene.add.container(0, 0, lineChildren);
      line.setDepth(depth);
      return {distance, halo, line};
    });

    const moteImages = [];
    const motes = buildZoneContourLoops(edges).flatMap((points) => {
      const geometry = buildThreadGeometry(points);
      const count = Math.max(1, Math.round(geometry.length / (hexSize * motesConfig.spacingFactor)));
      return Array.from({length: count}, (_, index) => {
        const image = this.scene.add.image(0, 0, MELEE_TEXTURE_KEYS.GLOW);
        image.setScale((hexSize * motesConfig.sizeFactor) / (image.width || 1));
        image.setTint(palette.mote);
        moteImages.push(image);
        return {image, geometry, phase: index / count};
      });
    });
    const motesContainer = this.scene.add.container(0, 0, moteImages);
    motesContainer.setDepth(depth);

    return {layers, motes, motesContainer, hexSize};
  }

  createTargetPops(targets, depth, hexSize) {
    return targets.map(({hex, distance}) => {
      const image = this.scene.add.image(hex.centerX, hex.centerY, CELL_OUTLINE_TEXTURE_KEYS.GLOW);
      image.setTint(RANGE_ZONE.palette.targetPop);
      image.setDepth(depth);
      image.setVisible(false);
      return {image, distance, baseScale: hexSize / CELL_OUTLINE_RADIUS_PX};
    });
  }

  updateZone(zone, now) {
    const {range, isQuiet, startedAt, dim, rings, hatch, layers, motes, motesContainer, hexSize, pops} = zone;
    const elapsedMs = now - startedAt;
    const revealMs = resolveRangeZoneRevealMs(range);
    const revealShare = clamp01(elapsedMs / revealMs);
    const waveRing = elapsedMs / RANGE_ZONE.wave.ringMs;

    dim.setAlpha(revealShare);
    hatch.setAlpha(revealShare);

    rings.forEach(({distance, graphics}) => {
      const alpha = resolveRingAlpha({elapsedMs, now, distance, range, isQuiet});
      graphics.setVisible(alpha > 0);
      graphics.setAlpha(alpha);
    });

    const haloAlpha = resolveContourHaloAlpha({elapsedMs, now, range});
    layers.forEach(({distance, halo, line}) => {
      const isReached = waveRing >= distance;
      halo.setVisible(isReached);
      line.setVisible(isReached);
      halo.setAlpha(haloAlpha);
    });

    const isRevealed = elapsedMs >= revealMs;
    motesContainer.setVisible(isRevealed);
    if (isRevealed) {
      const speed = hexSize * RANGE_ZONE.motes.speedFactor;
      motes.forEach(({image, geometry, phase}) => {
        const distance = (((now / 1000) * speed) / geometry.length + phase) % 1;
        const point = resolveThreadPoint(geometry, geometry.length * distance);
        image.setPosition(point.x, point.y);
      });
    }

    const {durationMs, maxScale} = RANGE_ZONE.targetPop;
    pops.forEach(({image, distance, baseScale}) => {
      const share = (elapsedMs - distance * RANGE_ZONE.wave.ringMs) / durationMs;
      const isPlaying = share >= 0 && share < 1;
      image.setVisible(isPlaying);
      if (isPlaying) {
        image.setScale(baseScale * (1 + (maxScale - 1) * share));
        image.setAlpha(1 - share);
      }
    });
  }

  tick() {
    if (this.zone) {
      this.updateZone(this.zone, this.now());
    }
  }

  /** Убрать зону: отряд больше не выбран */
  hide() {
    if (!this.zone) {
      return;
    }
    const {dim, rings, hatch, layers, motesContainer, pops} = this.zone;
    dim.destroy();
    rings.forEach(({graphics}) => graphics.destroy());
    hatch.destroy();
    layers.forEach(({halo, line}) => {
      halo.destroy();
      line.destroy();
    });
    motesContainer.destroy();
    pops.forEach(({image}) => image.destroy());
    this.zone = null;
    if (this.isListening) {
      this.scene?.events?.off?.('update', this.tick);
      this.isListening = false;
    }
  }

  /** Разбор поля */
  clear() {
    this.hide();
  }
}
